import logoVariants from '@/assets/img/logo';
import { svgIconPaths } from '@/assets/svg/icons';
import ColorOverlay from '@/components/atoms/ColorOverlay';
import CustomButton from '@/components/atoms/CustomButton';
import FilePicker from '@/components/atoms/FilePicker';
import SvgIcon from '@/components/atoms/SvgIcon';
import { Box, Center, Image } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const IMAGE_SIZE_PX = '200px';
const PROFILE_IMG_PLACEHOLDER_SIZE = 200;

type Props = {
  setImage: (file: File) => void;
  initialPreviewUrl: string;
  isLoading?: boolean;
};

const LogoImage = ({ initialPreviewUrl, setImage, isLoading }: Props) => {
  const [showUploadButton, setShowUploadButton] = useState(false);

  const inputProfilePhotoRef = useRef<HTMLInputElement>(null);

  const [pendingPreview, setPendingPreview] = useState<string | null>(initialPreviewUrl);
  const [preview, setPreview] = useState<string | null>();

  useEffect(() => {
    if (!isLoading) setPreview(pendingPreview);
  }, [isLoading, pendingPreview]);

  useEffect(() => {
    return () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    };
  }, [pendingPreview]);

  const hasInitImagePreview = !!pendingPreview || !!initialPreviewUrl;

  const setSelectedFile = useCallback(async (file: File | null) => {
    if (!file) return;

    setImage(file);
  }, []);

  return (
    <Center
      borderRadius={'50%'}
      backgroundImage={preview ?? ''}
      height={IMAGE_SIZE_PX}
      width={IMAGE_SIZE_PX}
      backgroundPosition={'center'}
      backgroundRepeat={'no-repeat'}
      backgroundSize={'cover'}
      onMouseEnter={() => setShowUploadButton(true)}
      onMouseLeave={() => setShowUploadButton(false)}
      borderWidth={'5px'}
      borderColor={'white'}
      boxShadow={'0px 6px 12px 0px #0E006224'}
      position={'relative'}
      overflow={'hidden'}
      onClick={() => inputProfilePhotoRef.current?.click()}
    >
      {isLoading ? (
        <>
          <ColorOverlay color1Alpha={0.7} color2Alpha={0.7} />
          <Image
            animation={'alternate-opacity 0.7s infinite ease-out alternate'}
            src={logoVariants.whiteNoText160x160}
            width={'90px'}
            height={'92px'}
            alt="my-omnia-logo"
          />
        </>
      ) : (
        <Box position={'absolute'} opacity={showUploadButton ? 1 : 0} transition={'opacity 0.3s'}>
          <FilePicker
            inputRef={inputProfilePhotoRef}
            selectedFile={null}
            setSelectedFile={setSelectedFile}
            setPreview={setPendingPreview}
            accept="image/*"
            fileMinHeightPx={100}
            fileMinWidthPx={100}
          />
          <CustomButton
            // just sending in an empty function, because <Center> parent handles the click
            onClick={() => {}}
            label={'Upload'}
            backgroundColor="primary.600"
            hoverBackgroundColor="primary.600"
            labelHoverColor="white"
            labelColor="white"
            isLoading={isLoading}
          />
        </Box>
      )}
      {!hasInitImagePreview && (
        <Center
          backgroundColor={'primary.100'}
          height={PROFILE_IMG_PLACEHOLDER_SIZE}
          width={PROFILE_IMG_PLACEHOLDER_SIZE}
        >
          <SvgIcon color={'#fff'} iconPath={svgIconPaths.userSeeThrough} size={80} />
        </Center>
      )}
    </Center>
  );
};

export default LogoImage;
